import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '../api/client'

/**
 * De voortgang van de "Aan de slag"-stappen, gedeeld door het startscherm
 * en de voortgangschip in de beheerbalk.
 *
 * De server méét de stappen (het /account/onboarding-endpoint kijkt naar wat
 * er echt in het account staat); hier wordt alleen vertaald naar de vijf
 * rijen die de gebruiker ziet. Bewust een store en niet component-state:
 * meerdere plekken tonen dezelfde voortgang en er hoort maar één verzoek
 * per paginaweergave uit te gaan.
 */

export interface OnboardingItem {
  key: string
  done: boolean
  route: string
  optional: boolean
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const steps = ref<Record<string, boolean> | null>(null)
  const loaded = ref(false)
  let lopend: Promise<void> | null = null

  /**
   * De zes gemeten stappen, gebundeld tot vijf rijen: categorie + pagina
   * zijn voor de gebruiker één handeling ("maak je eerste pagina").
   *
   * Let op de sleutelnamen: de API antwoordt in snake_case, maar de
   * api-client zet elk gelukt antwoord om naar camelCase - hier komen de
   * stappen dus als figmaConnected enz. binnen. (De oude checklist las
   * snake_case en toonde daardoor altijd nul afgevinkte stappen; dat
   * viel nooit op omdat het scherm waar hij op stond onbereikbaar was.)
   */
  const items = computed<OnboardingItem[]>(() => {
    const s = steps.value
    if (!s) return []

    return [
      { key: 'figma', done: !!s.figmaConnected, route: '/figma', optional: true },
      { key: 'import', done: !!s.figmaFileImported, route: '/figma', optional: true },
      { key: 'page', done: !!(s.sectionCreated && s.pageCreated), route: '/pages', optional: false },
      { key: 'publish', done: !!s.pagePublished, route: '/pages', optional: false },
      { key: 'team', done: !!s.teamInvited, route: '/account/team', optional: false },
    ]
  })

  const doneCount = computed(() => items.value.filter((i) => i.done).length)
  const total = computed(() => items.value.length)
  const allDone = computed(() => total.value > 0 && doneCount.value === total.value)

  /**
   * Voortgang ophalen. Eén keer per sessie is genoeg (force haalt opnieuw);
   * gelijktijdige aanroepen vanaf meerdere plekken delen hetzelfde verzoek.
   */
  async function fetchSteps(force = false): Promise<void> {
    if (loaded.value && !force) return
    if (lopend) return lopend

    lopend = api
      .get('/account/onboarding')
      .then((response) => {
        steps.value = response.data.steps ?? null
      })
      .catch(() => {
        // Geen voortgang kunnen ophalen is geen ramp: het scherm werkt
        // gewoon zonder checklist en chip.
        steps.value = null
      })
      .finally(() => {
        loaded.value = true
        lopend = null
      })

    return lopend
  }

  return { steps, loaded, items, doneCount, total, allDone, fetchSteps }
})
